import { Button } from "antd";
import dayjs from "dayjs";
import {
  DateRangeFilter,
  InputSelectAdditional,
  type IStatusAdditional,
} from "@shared/ui";
import { getRecordsRegisterStatusView } from "../../../../model";
import type { FilterState } from "../../../../model/hooks/use-filter-state";
import {
  MOCK_ACT_TYPE_OPTIONS,
  MOCK_SIGNER_OPTIONS,
  MOCK_STATUS_OPTIONS,
  MOCK_WORK_TYPE_OPTIONS,
} from "../../../../model/mock-data";
import { RegistryStatusPill } from "../registry-status-pill";
import "./registry-filters.scss";

function StatusMenuItem({ status }: { status: IStatusAdditional }) {
  const view = getRecordsRegisterStatusView(status.id);
  return (
    <span className="registry-filters__status-menu-item">
      <RegistryStatusPill
        textColor={view.textColor}
        backgroundColor={view.backgroundColor}
        className="registry-filters__status-menu-pill"
      >
        {view.label}
      </RegistryStatusPill>
      <span className="registry-filters__status-menu-count">
        {typeof status.count === "number" ? status.count : 0}
      </span>
    </span>
  );
}

function StatusSelectedItem({
  status,
  onRemove,
}: {
  status: IStatusAdditional;
  onRemove: () => void;
}) {
  const view = getRecordsRegisterStatusView(status.id);
  return (
    <span className="registry-filters__status-selected">
      <RegistryStatusPill
        textColor={view.textColor}
        backgroundColor={view.backgroundColor}
        className="registry-filters__status-selected-pill"
      >
        {view.label}
      </RegistryStatusPill>
      <button
        type="button"
        className="registry-filters__status-selected-remove"
        aria-label="Удалить"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onRemove();
        }}
      >
        ×
      </button>
    </span>
  );
}

interface RegistryFiltersProps {
  filterState: FilterState;
  showExtra: boolean;
  onToggleExtra: () => void;
}

export function RegistryFilters({ filterState, showExtra, onToggleExtra }: RegistryFiltersProps) {
  const { basic, extra } = filterState;

  return (
    <div className="registry-filters">
      <div className="registry-filters__header">
        <span className="registry-filters__label">Фильтрация</span>
        <Button type="link" className="registry-filters__toggle-btn" onClick={onToggleExtra}>
          {showExtra ? "Меньше фильтров" : "Больше фильтров"}
        </Button>
      </div>

      <div className="registry-filters__wrap">
        <div className="registry-filters__row">
          <InputSelectAdditional
            title="Тип работы"
            isMultiple
            statuses={MOCK_WORK_TYPE_OPTIONS}
            selectedStatuses={basic.workType}
            setSelectedStatuses={basic.setWorkType}
          />
          <DateRangeFilter
            placeholder="Дата акта"
            defaultValue={[
              basic.dateStart ? dayjs(basic.dateStart) : null,
              basic.dateEnd ? dayjs(basic.dateEnd) : null,
            ]}
            onChange={(range) => {
              basic.setDateStart(range?.[0] ? range[0].format("YYYY-MM-DD") : "");
              basic.setDateEnd(range?.[1] ? range[1].format("YYYY-MM-DD") : "");
            }}
          />
          <InputSelectAdditional
            title="Тип акта"
            isMultiple
            statuses={MOCK_ACT_TYPE_OPTIONS}
            selectedStatuses={basic.actType}
            setSelectedStatuses={basic.setActType}
          />
          <InputSelectAdditional
            title="Подписант"
            isMultiple
            statuses={MOCK_SIGNER_OPTIONS}
            selectedStatuses={basic.signer}
            setSelectedStatuses={basic.setSigner}
          />
          <InputSelectAdditional
            title="Статус записи"
            isMultiple
            statuses={MOCK_STATUS_OPTIONS}
            selectedStatuses={basic.statuses}
            setSelectedStatuses={basic.setStatuses}
            showAllOption
            renderMenuItem={(args) => <StatusMenuItem {...args} />}
            renderSelectedItem={(args) => <StatusSelectedItem {...args} />}
          />
        </div>

        {showExtra && (
          <div className="registry-filters__row">
            <InputSelectAdditional
              title="Действия в акте"
              isMultiple
              statuses={[]}
              selectedStatuses={extra.actAction}
              setSelectedStatuses={extra.setActAction}
            />
            <InputSelectAdditional
              title="Номер акта"
              isMultiple
              statuses={[]}
              selectedStatuses={extra.actNumber}
              setSelectedStatuses={extra.setActNumber}
            />
            <InputSelectAdditional
              title="Дата акта"
              isMultiple
              statuses={[]}
              selectedStatuses={extra.actDate}
              setSelectedStatuses={extra.setActDate}
            />
            <InputSelectAdditional
              title="Наименование работы"
              isMultiple
              statuses={[]}
              selectedStatuses={extra.workName}
              setSelectedStatuses={extra.setWorkName}
            />
            <InputSelectAdditional
              title="Вид работы"
              isMultiple
              statuses={MOCK_WORK_TYPE_OPTIONS}
              selectedStatuses={extra.workKind}
              setSelectedStatuses={extra.setWorkKind}
            />
            <InputSelectAdditional
              title="Замечания"
              isMultiple
              statuses={[]}
              selectedStatuses={extra.notes}
              setSelectedStatuses={extra.setNotes}
            />
            <InputSelectAdditional
              title="Закрытие"
              isMultiple
              statuses={[]}
              selectedStatuses={extra.closing}
              setSelectedStatuses={extra.setClosing}
            />
            <InputSelectAdditional
              title="Автор"
              isMultiple
              statuses={MOCK_SIGNER_OPTIONS}
              selectedStatuses={extra.author}
              setSelectedStatuses={extra.setAuthor}
            />
            <InputSelectAdditional
              title="Проверяющий"
              isMultiple
              statuses={MOCK_SIGNER_OPTIONS}
              selectedStatuses={extra.inspector}
              setSelectedStatuses={extra.setInspector}
            />
            <InputSelectAdditional
              title="Раздел проекта"
              isMultiple
              statuses={[]}
              selectedStatuses={extra.section}
              setSelectedStatuses={extra.setSection}
            />
            <InputSelectAdditional
              title="Конструкция"
              isMultiple
              statuses={[]}
              selectedStatuses={extra.construction}
              setSelectedStatuses={extra.setConstruction}
            />
            <InputSelectAdditional
              title="Договор"
              isMultiple
              statuses={[]}
              selectedStatuses={extra.contract}
              setSelectedStatuses={extra.setContract}
            />
            <InputSelectAdditional
              title="КС-2"
              isMultiple
              statuses={[]}
              selectedStatuses={extra.ks2}
              setSelectedStatuses={extra.setKs2}
            />
          </div>
        )}
      </div>
    </div>
  );
}
